import React, { useContext, useState } from "react"
import { FormattedMessage } from "react-intl"
import Modal from "react-bootstrap/Modal"
import { FirebaseContext } from "../Firebase"
import { sendStandardToast } from "../toasts/standard-toast"
import SubmitButton from "../portfolio/SubmitButton"
import PasswordInput from "../forms/PasswordInput"
import ReauthenticateModal from "./ReauthenticateModal"

/**
 * Modal for allowing a user to add a password to their account
 * @param {boolean} show - whether the modal is open
 * @param {Function} afterPasswordAdded - callback that runs after a password is successfully added
 * @param {Function} handleClose - runs when modal is manually closed
 * @param {Function} handleOpen - opens the modal
 * @returns {JSX}
 */
const AddPasswordModal = ({ show, afterPasswordAdded, handleClose, handleOpen }) => {
  const { firebase } = useContext(FirebaseContext)
  const [password, setPassword] = useState("")
  const [reauthModalOpen, setReauthModalOpen] = useState("")

  const onPasswordModalClose = () => {
    handleClose()
    setPassword("")
  }

  const handlePasswordAdd = async () => {
    try {
      const passwordCredential = firebase.app.auth.EmailAuthProvider.credential(
        firebase.auth.currentUser.email,
        password,
      )
      await firebase.auth.currentUser.linkWithCredential(passwordCredential)
    } catch (e) {
      if (e.code === "auth/requires-recent-login") {
        setReauthModalOpen(true)
        handleClose()
      } else {
        console.log("error when adding password", e.code, e)
        sendStandardToast({
          heading: `Error when trying to save password`,
          message: e.message,
        })
        handleOpen()
      }
      return
    }

    sendStandardToast({
      heading: "Successfully added a password to your account",
      type: "success",
    })

    await afterPasswordAdded()
    handleClose()
  }

  return (
    <>
    <Modal show={show && !reauthModalOpen} onHide={onPasswordModalClose} size="md">
      <Modal.Header closeButton>
        <div className="eyebrow component-eyebrow">
          <FormattedMessage id="account-page-choose-password" defaultMessage="Choose Password" />
        </div>
      </Modal.Header>
      <Modal.Body closeButton>
        <p>
          <FormattedMessage
            id="account-page-please-choose-password"
            defaultMessage="Please choose a password to use when you sign in with your personal email"
          />
        </p>
        <PasswordInput
          password={password}
          onPasswordChange={(newPassword) => setPassword(newPassword)}
          isNew={true}
        />
      </Modal.Body>    
      <Modal.Footer>
        <SubmitButton
          label={<FormattedMessage id="account-page-save-password" defaultMessage="Save Password" />}
          onClick={handlePasswordAdd}
          className="account-page-submit-button"
        />
      </Modal.Footer>
    </Modal>
    <ReauthenticateModal
      show={reauthModalOpen}
      onReauthSuccess={handlePasswordAdd}
      handleClose={() => setReauthModalOpen(false)}
    />
    </>
  )
}

export default AddPasswordModal
